/**
 * Shared risk-band utilities.
 *
 * The risk scale is 1.0-5.0, one decimal place, LOWER IS BETTER — a weighted
 * mean of six Gemini-scored facets (see backend/app/workers/risk.py).
 *
 * Bands are derived from the entity's configurable `alertThreshold`
 * (RiskConfig.alertThreshold — range 2.0-4.5, default 3.0; see
 * frontend/src/types/index.ts), never from a hardcoded global cut-point.
 * `alertThreshold` already means "flag this project as At Risk" — customers
 * configure it themselves in RiskConfiguration.tsx, so re-deriving fixed
 * bands elsewhere would silently reinterpret a setting they already chose.
 *
 * Keep this file behaviourally identical to backend/app/utils/risk_bands.py.
 * A mismatch is customer-visible: alert emails, Slack blocks, and AI prompts
 * render server-side from the same score the UI renders client-side.
 */

export type RiskBand = 'high' | 'medium' | 'low';

/** Mirrors RiskConfig.alertThreshold's default in frontend/src/types/index.ts. */
export const DEFAULT_ALERT_THRESHOLD = 3.0;

/** Scale bounds — see risk.py's weighted-mean formula. */
export const MIN_SCORE = 1.0;
export const MAX_SCORE = 5.0;

export interface RiskBandStyle {
  band: RiskBand;
  label: string;
  /** Text-only tone — matches the High/Medium/Low convention in DesignerDashboard.tsx. */
  textClass: string;
  /** Solid WCAG AA background + text — matches RiskBadge.tsx / StatusChip.tsx convention. */
  bgClass: string;
}

const BAND_STYLES: Record<RiskBand, RiskBandStyle> = {
  high: {
    band: 'high',
    label: 'High',
    textClass: 'text-red-600 dark:text-red-400',
    bgClass: 'bg-red-600 text-white',
  },
  medium: {
    band: 'medium',
    label: 'Medium',
    textClass: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-400 text-gray-900',
  },
  low: {
    band: 'low',
    label: 'Low',
    textClass: 'text-green-600 dark:text-green-400',
    bgClass: 'bg-green-500 text-white',
  },
};

/**
 * Classify a risk score into a band, relative to the entity's alertThreshold.
 *
 *   High   — score >= alertThreshold
 *   Medium — score >= alertThreshold - 1.0 (and below High)
 *   Low    — below that
 *
 * Edge cases (deliberate — keep in sync with risk_bands.py):
 *
 *  - null/undefined score -> not yet evaluated. Returns null; callers should
 *    render a neutral "no score" state, not a band.
 *
 *  - score === 0 -> the "unscored" sentinel a project can carry when it has
 *    fewer than minTicketsForScore tickets (see risk.py). 0 is below the
 *    valid 1.0-5.0 scale and must NOT be silently banded as "extremely Low
 *    risk" — that would read as a false all-clear. Treated the same as no
 *    score: returns null.
 *
 *  - score outside [1.0, 5.0] but nonzero (e.g. upstream rounding drift or a
 *    future scale change) -> clamp into range rather than discard it. A
 *    malformed-but-present score is more useful surfaced (even if the band
 *    is slightly off at the boundary) than silently vanishing from an alert.
 *
 *  - alertThreshold === 2.0 (its configured minimum) -> Medium's lower bound
 *    is threshold - 1.0 === 1.0, the scale floor, which makes Low
 *    mathematically unreachable. This is intentional, not a bug: 2.0 is the
 *    most sensitive threshold a customer can pick, and correctly means
 *    "treat anything above the floor as at least Medium risk." Do not
 *    special-case it to manufacture an artificial Low band — that would
 *    override the customer's explicit choice.
 */
export function getRiskBand(
  score: number | null | undefined,
  alertThreshold: number = DEFAULT_ALERT_THRESHOLD,
): RiskBand | null {
  if (score === null || score === undefined) return null;
  if (score === 0) return null;

  const clamped = Math.min(MAX_SCORE, Math.max(MIN_SCORE, score));
  const mediumFloor = alertThreshold - 1.0;

  if (clamped >= alertThreshold) return 'high';
  if (clamped >= mediumFloor) return 'medium';
  return 'low';
}

/** Style tokens for a band, or null when there is no band (no/unscored data). */
export function getRiskBandStyle(band: RiskBand | null): RiskBandStyle | null {
  return band ? BAND_STYLES[band] : null;
}

/** Raw score formatted to one decimal, or an em dash placeholder when absent/unscored. */
export function formatRiskScore(score: number | null | undefined): string {
  if (score === null || score === undefined || score === 0) return '—';
  return score.toFixed(1);
}

/**
 * Bundle for callers that need band, display style, and the raw score
 * together — the number becomes secondary (e.g. shown on hover) rather than
 * disappearing once a band is assigned.
 */
export interface RiskBandDisplay {
  band: RiskBand | null;
  label: string;
  textClass: string;
  bgClass: string;
  scoreLabel: string;
}

export function getRiskBandDisplay(
  score: number | null | undefined,
  alertThreshold: number = DEFAULT_ALERT_THRESHOLD,
): RiskBandDisplay {
  const band = getRiskBand(score, alertThreshold);
  const style = getRiskBandStyle(band);
  return {
    band,
    label: style?.label ?? 'No score',
    textClass: style?.textClass ?? 'text-gray-400 dark:text-gray-500',
    bgClass: style?.bgClass ?? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200',
    scoreLabel: formatRiskScore(score),
  };
}
